import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ForceGraph2D } from 'react-force-graph';
import { apiService } from '../api/apiClient';
import { useTranslation } from 'react-i18next';
import { useCanvasColors } from '../theme/canvasHelpers';

/** Fixed palettes — every node/edge color must come from these so the legend stays truthful. */
const NODE_COLOR_PALETTE = [
  '#ef4444', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6',
  '#06b6d4', '#f97316', '#ec4899', '#84cc16', '#6366f1'
];

const RELATIONSHIP_COLOR_PALETTE = [
  '#64748b', '#dc2626', '#2563eb', '#059669', '#d97706',
  '#7c2d12', '#c2410c', '#b91c1c', '#7c3aed', '#0891b2'
];

function hashString(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = s.charCodeAt(i) + ((h << 5) - h);
  }
  return Math.abs(h);
}

/** Deterministic color from palette only (no random HSL — matches legend). */
function paletteColorForString(str, palette) {
  const p = palette?.length ? palette : NODE_COLOR_PALETTE;
  if (!str || str === 'undefined' || str === 'null') return p[0];
  return p[hashString(String(str)) % p.length];
}

/** Escape string for safe embedding in Cypher single-quoted literals. */
function escapeCypherString(s) {
  return String(s).replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

/** Wikidata-style PIDs and common keys — ETL stores titles/authors as P1476/P50, not title/name. */
function pickNeo4jDisplayLabel(row) {
  if (!row || typeof row !== 'object') return '';
  const keys = [
    'displayLabel',
    'text',
    'name',
    'title',
    'p1476',
    'p50',
    'P1476',
    'P50',
    'P131',
    'P17',
  ];
  for (const k of keys) {
    const v = row[k];
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  const props = row.props;
  if (props && typeof props === 'object') {
    for (const k of ['P1476', 'P50', 'P131', 'name', 'title', 'text']) {
      const v = props[k];
      if (v != null && String(v).trim() !== '') return String(v).trim();
    }
  }
  return '';
}

/**
 * Resolve article/chat source ref (internal id, wikidata IRI, QID) to Neo4j internal id string.
 */
async function resolveRefToNeo4jInternalId(ref) {
  const s = String(ref).trim();
  if (!s) return null;
  const safe = escapeCypherString(s);
  const lastSeg = escapeCypherString(s.split('/').pop() || s);
  const qid = s.match(/(Q\d+)/i)?.[1];
  const qidEsc = qid ? escapeCypherString(qid) : '';
  const query = `
    MATCH (n)
    WHERE elementId(n) = '${safe}'
       OR n.wikidata_id = '${safe}'
       OR n.wikidata_id ENDS WITH '${lastSeg}'
       ${qidEsc ? `OR n.wikidata_id ENDS WITH '${qidEsc}'` : ''}
    RETURN elementId(n) AS id LIMIT 1
  `;
  try {
    const res = await apiService.executeSparql(query);
    const row = res?.results?.[0];
    if (row && row.id !== undefined && row.id !== null) return String(row.id);
  } catch (e) {
    console.warn('resolveRefToNeo4jInternalId:', e);
  }
  return null;
}

/**
 * Panel title: prefer domain title/name from loaded props; graph label may be only the internal id.
 */
function panelHeaderTitle(selectedNode, nodeProps) {
  if (!selectedNode) return '';
  const map = Object.fromEntries(nodeProps || []);
  const fromProps = String(
    map.title || map.name || map.P1476 || map.text || ''
  ).trim();
  if (fromProps) return fromProps;
  const l = String(selectedNode.label || '').trim();
  if (l && l !== String(selectedNode.id)) return l;
  return l || `Node ${selectedNode.id}`;
}

/** Catalog / site record id (641) vs Neo4j internal id (17) — uses props.id when it differs from id(n). */
function recordCatalogIdFromProps(map, internalId) {
  const cat = map.catalogCode ?? map.catalog_code;
  if (cat != null && String(cat).trim() !== '') return String(cat).trim();
  const idp = map.id;
  if (idp != null && String(idp).trim() !== '' && String(idp) !== String(internalId)) {
    return String(idp).trim();
  }
  return null;
}

/** Same logic as loadGraphData node typing — used after fetching labels(n) for off-graph selection. */
function mapNeo4jLabelsToNodeType(labels) {
  if (!labels || !Array.isArray(labels) || labels.length === 0) return 'unknown';
  if (labels.includes('Person')) return 'Person';
  if (labels.includes('MassGrave')) return 'MassGrave';
  if (labels.includes('Publication')) return 'Publication';
  if (labels.includes('Document')) return 'Document';
  const primary = labels[0];
  if (primary && primary !== 'Unknown') return primary;
  return 'unknown';
}

/** Topbar height in App — canvas graph height subtracts this so the canvas fills the main area. */
const GRAPH_TOPBAR_OFFSET_PX = 48;

const RDFView = ({
  fullscreen = false,
  canvas = false,
  focusNodeRef = null,
  onFocusNodeConsumed,
  onSelectionChange,
}) => {
  const { t } = useTranslation();
  const canvasColors = useCanvasColors();
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('graph'); // 'graph' or 'stats'
  const [dynamicColors, setDynamicColors] = useState({
    nodes: {},
    relationships: {}
  });
  const [stats, setStats] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [nodeProps, setNodeProps] = useState([]);
  const [nodeNeighbors, setNodeNeighbors] = useState({ outgoing: [], incoming: [] });
  const [propsLoading, setPropsLoading] = useState(false);
  /** Node id with an animated ring: 'sticky' until any pointerdown, or 'timed' for a short flash */
  const [pulseTargetId, setPulseTargetId] = useState(null);
  const [pulseAnim, setPulseAnim] = useState(0);
  const pulseModeRef = useRef('sticky');
  const pulseTimerRef = useRef(null);
  const pulseTargetIdRef = useRef(null);
  const fgRef = useRef();
  const focusHandledForRef = useRef(null);
  const graphDataRef = useRef(graphData);
  graphDataRef.current = graphData;

  /** Keep graph dimensions in sync with viewport (mobile rotation, URL bar show/hide). */
  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 800,
    h: typeof window !== 'undefined' ? window.innerHeight : 800,
  }));

  /** Touch / narrow screens: disable dragging nodes so one-finger drag pans the graph like desktop. */
  const [coarsePointer, setCoarsePointer] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches
  );

  useEffect(() => {
    const onResize = () => {
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    };
    onResize();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(pointer: coarse)');
    const onChange = () => setCoarsePointer(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const touchLikeGraphNav = useMemo(
    () => coarsePointer || viewport.w < 640,
    [coarsePointer, viewport.w]
  );

  const canvasGraphWidth = viewport.w;
  const canvasGraphHeight = Math.max(200, viewport.h - GRAPH_TOPBAR_OFFSET_PX);

  const panelIdentity = useMemo(() => {
    if (!selectedNode) return { title: '', recordId: null };
    const map = Object.fromEntries(nodeProps);
    return {
      title: panelHeaderTitle(selectedNode, nodeProps),
      recordId: recordCatalogIdFromProps(map, selectedNode.id),
    };
  }, [selectedNode, nodeProps]);

  useEffect(() => {
    pulseTargetIdRef.current = pulseTargetId;
  }, [pulseTargetId]);

  useEffect(() => {
    if (pulseTargetId == null) return;
    const id = window.setInterval(() => setPulseAnim((x) => x + 1), 48);
    return () => window.clearInterval(id);
  }, [pulseTargetId]);

  useEffect(() => {
    const onPointerDown = () => {
      if (pulseModeRef.current !== 'sticky') return;
      if (pulseTargetIdRef.current == null) return;
      if (pulseTimerRef.current) {
        window.clearTimeout(pulseTimerRef.current);
        pulseTimerRef.current = null;
      }
      setPulseTargetId(null);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, []);

  const startPulse = useCallback((id, mode = 'sticky') => {
    if (id == null) return;
    const sid = String(id);
    if (pulseTimerRef.current) {
      window.clearTimeout(pulseTimerRef.current);
      pulseTimerRef.current = null;
    }
    pulseModeRef.current = mode;
    setPulseTargetId(sid);
    if (mode === 'timed') {
      pulseTimerRef.current = window.setTimeout(() => {
        pulseTimerRef.current = null;
        setPulseTargetId((cur) => (cur === sid ? null : cur));
      }, 900);
    }
  }, []);

  const handleLinkClick = useCallback(
    (link) => {
      const pick = (end) => {
        if (end == null) return null;
        if (typeof end === 'object' && end.id != null) return String(end.id);
        return String(end);
      };
      const targetId = pick(link?.target);
      if (targetId) startPulse(targetId, 'sticky');
    },
    [startPulse]
  );

  const zoomControlClass =
    'min-w-[2.5rem] min-h-[2.5rem] inline-flex items-center justify-center rounded-lg ' +
    'bg-surface-raised/95 border border-border-strong text-ink text-base font-semibold ' +
    'shadow-md hover:bg-surface-hover hover:border-accent/40 focus:outline-none focus:ring-2 focus:ring-accent/50';

  // Los colores ahora se generan dinámicamente desde Neo4j en loadGraphData

  // Listen for refresh events
  useEffect(() => {
    const handleRefresh = () => {
      loadGraphData();
    };

    window.addEventListener('refreshGraph', handleRefresh);
    return () => window.removeEventListener('refreshGraph', handleRefresh);
  }, []);

  // Cargar datos del grafo
  const loadGraphData = useCallback(async () => {
    console.log('RDFView: Starting loadGraphData...');
    setLoading(true);
    setError(null);

    try {
      // Obtener estadísticas usando Cypher (Neo4j native)
      const statsQuery = `
        MATCH (n)
        OPTIONAL MATCH ()-[r]->()
        RETURN count(DISTINCT n) as totalNodes, count(r) as totalRelationships
      `;

      // Consulta para obtener todos los tipos de nodos (labels)
      const typesQuery = `
        MATCH (n)
        UNWIND labels(n) as label
        RETURN label, count(*) as count
        ORDER BY count DESC
      `;

      // Consulta para obtener todos los tipos de relaciones
      const relationsQuery = `
        MATCH ()-[r]->()
        RETURN type(r) as relationshipType, count(*) as count
        ORDER BY count DESC
      `;

      const statsResponse = await apiService.executeSparql(statsQuery);
      const typesResponse = await apiService.executeSparql(typesQuery);
      const relationsResponse = await apiService.executeSparql(relationsQuery);

      // Procesar tipos de nodos encontrados (Cypher format)
      let nodeTypes = [];
      if (typesResponse?.results && Array.isArray(typesResponse.results)) {
        nodeTypes = typesResponse.results.map(result => ({
          uri: result.label || '',
          count: parseInt(result.count || 0),
          shortName: (result.label || '').toString().toLowerCase() || 'unknown'
        }));
      }

      // Procesar tipos de relaciones encontrados (Cypher format)
      let relationTypes = [];
      if (relationsResponse?.results && Array.isArray(relationsResponse.results)) {
        relationTypes = relationsResponse.results.map(result => ({
          uri: result.relationshipType || '',
          count: parseInt(result.count || 0),
          shortName: (result.relationshipType || '').toString().toLowerCase() || 'unknown'
        }));
      }

      // Generar colores dinámicos separados para nodos y relaciones (misma paleta que los fallbacks)
      const nodeColors = {};
      const relationshipColors = {};

      // Asignar colores a nodos usando los tipos reales de Neo4j
      nodeTypes.forEach((nodeType, index) => {
        const typeKey = nodeType.shortName.toLowerCase().replace(/[^a-z0-9]/g, '_');
        nodeColors[typeKey] = NODE_COLOR_PALETTE[index % NODE_COLOR_PALETTE.length];
      });

      // Asignar colores a relaciones usando los tipos reales de Neo4j
      relationTypes.forEach((relType, index) => {
        const typeKey = relType.shortName.toLowerCase().replace(/[^a-z0-9]/g, '_');
        relationshipColors[typeKey] = RELATIONSHIP_COLOR_PALETTE[index % RELATIONSHIP_COLOR_PALETTE.length];
      });

      // Actualizar colores dinámicos separados (para la leyenda)
      setDynamicColors({
        nodes: nodeColors,
        relationships: relationshipColors
      });

      // Helpers locales que usan las variables recién calculadas,
      // evitando el stale closure de dynamicColors (que aún no se actualizó)
      const resolveNodeColor = (typeUri) => {
        const typeString = String(typeUri || '');
        if (typeString) {
          const typeKey = typeString.split(/[/|#]/).pop() || '';
          const normalizedKey = typeKey.toLowerCase().replace(/[^a-z0-9]/g, '_');
          if (nodeColors[normalizedKey]) return nodeColors[normalizedKey];
          for (const [k, c] of Object.entries(nodeColors)) {
            if (normalizedKey.includes(k) || k.includes(normalizedKey)) return c;
          }
        }
        if (typeString && typeString !== 'undefined' && typeString !== 'null') {
          return paletteColorForString(typeString, NODE_COLOR_PALETTE);
        }
        return NODE_COLOR_PALETTE[0];
      };

      const resolveRelationshipColor = (relType) => {
        const typeString = String(relType || '');
        if (typeString) {
          const normalizedKey = typeString.toLowerCase().replace(/[^a-z0-9]/g, '_');
          if (relationshipColors[normalizedKey]) return relationshipColors[normalizedKey];
          for (const [k, c] of Object.entries(relationshipColors)) {
            if (normalizedKey.includes(k) || k.includes(normalizedKey)) return c;
          }
        }
        if (typeString && typeString !== 'undefined' && typeString !== 'null') {
          return paletteColorForString(typeString, RELATIONSHIP_COLOR_PALETTE);
        }
        return RELATIONSHIP_COLOR_PALETTE[0];
      };

      // Handle Neo4j response structure for stats
      let statsData = { total: 0, relationships: 0 };

      if (statsResponse?.results && Array.isArray(statsResponse.results) && statsResponse.results.length > 0) {
        const result = statsResponse.results[0];
        statsData = {
          total: parseInt(result.totalCount || 0),
          relationships: parseInt(result.relationshipsCount || 0)
        };
      }

      setStats(statsData);

      // Obtener nodos usando Cypher directamente
      const nodesQuery = `
        MATCH (n)
        RETURN elementId(n) AS id, labels(n) AS labels, properties(n) AS props
        LIMIT 4000
      `;

      const nodesResponse = await apiService.executeSparql(nodesQuery);

      // Handle Cypher response structure for nodes
      let nodes = [];
      if (nodesResponse?.results && Array.isArray(nodesResponse.results)) {
        console.log('RDFView: First node result sample:', nodesResponse.results[0]);
        nodes = nodesResponse.results
          .map((result) => {
            const props = result.props && typeof result.props === 'object' ? result.props : {};
            let labels = result.labels || [];
            let nodeType = 'unknown';

            if (typeof labels === 'string') labels = [labels];
            if (!Array.isArray(labels)) labels = [];

            const primaryLabel = labels.length > 0 ? labels[0] : 'Unknown';

            if (labels.length > 0) {
              if (labels.includes('Person')) nodeType = 'Person';
              else if (labels.includes('MassGrave')) nodeType = 'MassGrave';
              else if (labels.includes('Publication')) nodeType = 'Publication';
              else if (labels.includes('Document')) nodeType = 'Document';
              else if (primaryLabel !== 'Unknown') nodeType = primaryLabel;
            }

            if (nodeType === 'unknown' && props.type) {
              const typeValue = String(props.type);
              if (typeValue === 'Q5' || typeValue.includes('Person')) nodeType = 'Person';
              else if (typeValue === 'Q108163' || typeValue.includes('MassGrave')) nodeType = 'MassGrave';
              else if (typeValue === 'Q49848' || typeValue.includes('Document')) nodeType = 'Document';
              else nodeType = typeValue;
            }

            const nodeLabel = pickNeo4jDisplayLabel(props) || `Node ${result.id}`;

            return {
              id: String(result.id),
              label: String(nodeLabel).substring(0, 50),
              type: nodeType,
              val: 12,
              color: resolveNodeColor(nodeType),
              properties: { labels, ...props },
            };
          })
          .filter(node => node.id && node.label);
      }
      console.log('RDFView: Processed nodes:', nodes.length, 'Sample node:', nodes[0]);

      // Obtener relaciones usando Cypher
      const relationshipsQuery = `
        MATCH (source)-[r]->(target)
        RETURN elementId(source) AS sourceId, elementId(target) AS targetId, type(r) AS relationshipType
        LIMIT 5000
      `;

      const relationshipsResponse = await apiService.executeSparql(relationshipsQuery);

      // Handle Neo4j response structure for relationships
      let links = [];

      if (relationshipsResponse?.results && Array.isArray(relationshipsResponse.results)) {
        console.log('RDFView: First relationship result sample:', relationshipsResponse.results[0]);
        links = relationshipsResponse.results.map((result, index) => {
          const sourceId = String(result.sourceId || '');
          const targetId = String(result.targetId || '');
          const relationshipType = result.relationshipType || 'UNKNOWN_RELATIONSHIP';

          // Usar colores dinámicos para relaciones
          const linkColor = resolveRelationshipColor(relationshipType);

          return {
            source: sourceId,
            target: targetId,
            label: relationshipType,
            color: linkColor
          };
        }).filter(link => link && link.source && link.target);

        const nodeIds = new Set(nodes.map(n => n.id));
        links = links.filter(link => nodeIds.has(link.source) && nodeIds.has(link.target));
      }

      // Update stats based on actual data
      const updatedStats = {
        people: nodes.filter(n => String(n.type || '').includes('Q5')).length,
        graves: nodes.filter(n => String(n.type || '').includes('Q108163')).length,
        documents: nodes.filter(n => String(n.type || '').includes('Q49848')).length,
        relationships: links.length
      };
      setStats(updatedStats);

      setGraphData({ nodes, links });
      setTimeout(() => fgRef.current?.zoomToFit(400), 300);

    } catch (err) {
      console.error('Error loading graph data:', err);
      setError(`${t('content:graphLoadError')}: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Función auxiliar para obtener color de nodo basado en tipo
  const getNodeColor = (typeUri) => {
    const typeString = String(typeUri || '');

    // Usar colores dinámicos de nodos
    if (Object.keys(dynamicColors.nodes).length > 0 && typeString) {
      // Extraer el tipo del URI (última parte después de / o #)
      const typeParts = typeString.split(/[/|#]/);
      const typeKey = typeParts.length > 0 ? typeParts[typeParts.length - 1] : '';
      const normalizedTypeKey = typeKey ? typeKey.toLowerCase() : '';
      const normalizedKey = normalizedTypeKey.replace(/[^a-z0-9]/g, '_');

      // Buscar coincidencia exacta primero
      if (dynamicColors.nodes[normalizedKey]) {
        return dynamicColors.nodes[normalizedKey];
      }

      // Buscar coincidencia parcial
      for (const [key, color] of Object.entries(dynamicColors.nodes)) {
        if (normalizedKey.includes(key) || key.includes(normalizedKey)) {
          return color;
        }
      }
    }

    if (typeString && typeString !== 'undefined' && typeString !== 'null') {
      return paletteColorForString(typeString, NODE_COLOR_PALETTE);
    }
    return NODE_COLOR_PALETTE[0];
  };

  // Función para obtener color de relaciones
  const getRelationshipColor = (relType) => {
    const typeString = String(relType || '');

    // Usar colores dinámicos de relaciones
    if (Object.keys(dynamicColors.relationships).length > 0 && typeString) {
      // Normalizar el tipo de relación
      const normalizedKey = typeString.toLowerCase().replace(/[^a-z0-9]/g, '_');

      // Buscar coincidencia exacta primero
      if (dynamicColors.relationships[normalizedKey]) {
        return dynamicColors.relationships[normalizedKey];
      }

      // Buscar coincidencia parcial
      for (const [key, color] of Object.entries(dynamicColors.relationships)) {
        if (normalizedKey.includes(key) || key.includes(normalizedKey)) {
          return color;
        }
      }
    }

    if (typeString && typeString !== 'undefined' && typeString !== 'null') {
      return paletteColorForString(typeString, RELATIONSHIP_COLOR_PALETTE);
    }
    return RELATIONSHIP_COLOR_PALETTE[0];
  };

  // Configurar fuerzas del grafo
  useEffect(() => {
    if (fgRef.current && graphData.nodes.length > 0) {
      const fg = fgRef.current;

      fg.d3Force('charge').strength(-400);
      fg.d3Force('link').distance(140);
      fg.d3Force('center').strength(0.05);
      fg.d3ReheatSimulation();
    }
  }, [graphData]);

  // Cargar datos al montar el componente
  useEffect(() => {
    loadGraphData();
  }, [loadGraphData]);

  const handleNodeClick = useCallback(async (node) => {
    setSelectedNode(node);
    onSelectionChange?.(String(node.id));
    setNodeProps([]);
    setNodeNeighbors({ outgoing: [], incoming: [] });
    setPropsLoading(true);
    try {
      const nodeElemId = escapeCypherString(node.id);
      const query = `MATCH (n) WHERE elementId(n) = '${nodeElemId}' RETURN properties(n) AS props, labels(n) AS labels`;
      const outQ = `
        MATCH (n)-[r]->(m) WHERE elementId(n) = '${nodeElemId}'
        RETURN type(r) AS relationshipType, elementId(m) AS otherId, properties(m) AS otherProps
        LIMIT 40`;
      const inQ = `
        MATCH (m)-[r]->(n) WHERE elementId(n) = '${nodeElemId}'
        RETURN type(r) AS relationshipType, elementId(m) AS otherId, properties(m) AS otherProps
        LIMIT 40`;
      const [res, outRes, inRes] = await Promise.all([
        apiService.executeSparql(query),
        apiService.executeSparql(outQ),
        apiService.executeSparql(inQ),
      ]);
      const row0 = res?.results?.[0] || {};
      const props = row0.props || {};
      let neoLabels = row0.labels;
      if (typeof neoLabels === 'string') neoLabels = [neoLabels];
      if (!Array.isArray(neoLabels)) neoLabels = [];
      const derivedType = mapNeo4jLabelsToNodeType(neoLabels);
      const entries = Object.entries(props).filter(
        ([k, v]) => v !== null && v !== undefined && (k === '_source' || !k.startsWith('_'))
      );
      setNodeProps(entries);
      setSelectedNode((prev) => {
        if (!prev || String(prev.id) !== String(node.id)) return prev;
        return { ...prev, type: derivedType };
      });
      const mapRow = (row) => {
        const otherProps = row.otherProps && typeof row.otherProps === 'object' ? row.otherProps : {};
        return {
          rel: row.relationshipType || '—',
          otherId: String(row.otherId ?? ''),
          otherLabel: (pickNeo4jDisplayLabel(otherProps) || String(row.otherId || '')).substring(0, 80),
        };
      };
      setNodeNeighbors({
        outgoing: (outRes?.results || []).map(mapRow),
        incoming: (inRes?.results || []).map(mapRow),
      });
    } catch (err) {
      console.error('handleNodeClick failed:', err);
    } finally {
      setPropsLoading(false);
    }
  }, [onSelectionChange]);

  const selectRelatedNode = useCallback(
    (otherId) => {
      const sid = String(otherId);
      const found = graphData.nodes.find((n) => n.id === sid);
      if (found) {
        handleNodeClick(found);
        try {
          fgRef.current?.centerAt(found.x, found.y, 400);
        } catch (_) {}
      } else {
        handleNodeClick({
          id: sid,
          label: sid,
          type: 'unknown',
          val: 6,
          color: paletteColorForString('unknown', NODE_COLOR_PALETTE),
          properties: {},
        });
      }
      // Sticky pulse on the graph until the user clicks anywhere (see pointerdown listener)
      window.setTimeout(() => startPulse(sid, 'sticky'), 80);
    },
    [graphData.nodes, handleNodeClick, startPulse]
  );

  // Open graph from Artículos with a node ref (wait until graph finished loading)
  useEffect(() => {
    if (!focusNodeRef || loading) return;
    if (focusHandledForRef.current === focusNodeRef) return;
    let cancelled = false;
    (async () => {
      const internalId = await resolveRefToNeo4jInternalId(focusNodeRef);
      if (cancelled) return;
      if (!internalId) {
        focusHandledForRef.current = focusNodeRef;
        onFocusNodeConsumed?.();
        return;
      }
      const sid = String(internalId);
      const nodes = graphDataRef.current?.nodes || [];
      const found = nodes.find((n) => n.id === sid);
      if (found) {
        await handleNodeClick(found);
        setTimeout(() => {
          try {
            fgRef.current?.centerAt(found.x, found.y, 500);
          } catch (_) {}
        }, 120);
      } else {
        await handleNodeClick({
          id: sid,
          label: String(focusNodeRef).substring(0, 80),
          type: 'unknown',
          val: 6,
          color: paletteColorForString('unknown', NODE_COLOR_PALETTE),
          properties: {},
        });
        setTimeout(() => {
          try {
            fgRef.current?.zoomToFit(400);
          } catch (_) {}
        }, 200);
      }
      window.setTimeout(() => startPulse(sid, 'sticky'), 80);
      focusHandledForRef.current = focusNodeRef;
      onFocusNodeConsumed?.();
    })();
    return () => {
      cancelled = true;
    };
  }, [focusNodeRef, loading, handleNodeClick, onFocusNodeConsumed, startPulse]);

  useEffect(() => {
    if (!focusNodeRef) focusHandledForRef.current = null;
  }, [focusNodeRef]);

  const ENTITY_TYPE_LABELS = {
    'Person': 'Brigadista',
    'Q5': 'Brigadista',
    'MassGrave': 'Fossa',
    'Q108163': 'Fossa',
    'Document': 'Document',
    'Publication': 'Document',
    'Q49848': 'Document',
  };

  const getEntityTypeLabel = (node) => {
    const typeStr = String(node?.type || '');
    if (ENTITY_TYPE_LABELS[typeStr]) return ENTITY_TYPE_LABELS[typeStr];
    // Try matching Q-ID suffix from a full URI
    const qid = typeStr.split('/').pop();
    if (ENTITY_TYPE_LABELS[qid]) return ENTITY_TYPE_LABELS[qid];
    return typeStr || '';
  };

  const renderNodePropRow = (key, val) => {
    const displayVal = Array.isArray(val) ? val.join(', ') : String(val);
    const isWikidataId = key === 'wikidata_id' && /^Q\d+$/.test(String(val));
    const trimmed = displayVal.trim();
    const isHttp = /^https?:\/\//i.test(trimmed);
    let displayKey = key.replace(/_/g, ' ');
    if (key === 'id') {
      displayKey = `${displayKey} (${t('content:graphPropIdHint')})`;
    }
    if (key === '_source') {
      const cfg = val === 'sidbrint'
        ? { label: 'SIDBRINT', cls: 'bg-amber-500/20 text-amber-300 border border-amber-500/30' }
        : val === 'fosses-comunes'
        ? { label: 'Fosses Comunes', cls: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' }
        : { label: 'Inferit', cls: 'bg-gray-500/20 text-gray-400 border border-gray-500/30' };
      return (
        <>
          <p className="text-label text-ink-muted capitalize">font de dades</p>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${cfg.cls}`}>
            {cfg.label}
          </span>
        </>
      );
    }
    if (isWikidataId) {
      return (
        <>
          <p className="text-label text-ink-muted break-words capitalize">{displayKey}</p>
          <a
            href={`https://www.wikidata.org/entity/${val}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-accent-text hover:underline break-words"
          >
            {displayVal}
          </a>
        </>
      );
    }
    if (isHttp) {
      return (
        <>
          <p className="text-label text-ink-muted break-words capitalize">{displayKey}</p>
          <a
            href={trimmed}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => startPulse(selectedNode?.id, 'timed')}
            className="text-sm text-accent-text hover:underline break-words"
          >
            {displayVal}
          </a>
        </>
      );
    }
    return (
      <>
        <p className="text-label text-ink-muted break-words capitalize">{displayKey}</p>
        <p className="text-sm text-ink-secondary break-words">{displayVal}</p>
      </>
    );
  };

  const nodeCanvasObject = (node, ctx, globalScale) => {
    const label = node.label;
    const fontSize = 10 / globalScale;

    // Dibujar nodo
    ctx.beginPath();
    ctx.arc(node.x, node.y, node.val || 4, 0, 2 * Math.PI, false);
    ctx.fillStyle = node.color;
    ctx.fill();

    // Borde
    ctx.strokeStyle = node.color;
    ctx.lineWidth = 1 / globalScale;
    ctx.stroke();

    if (pulseTargetId != null && String(node.id) === String(pulseTargetId)) {
      const t = pulseAnim * 0.14;
      const wave = 0.5 + 0.5 * Math.sin(t);
      const alpha = 0.28 + wave * 0.62;
      const ringBoost = (4 + wave * 4) / globalScale;
      ctx.beginPath();
      ctx.arc(node.x, node.y, (node.val || 4) + ringBoost, 0, 2 * Math.PI, false);
      ctx.strokeStyle = canvasColors.pulseRing(alpha);
      ctx.lineWidth = (2.2 + wave * 2.2) / globalScale;
      ctx.stroke();
    }

    // Etiqueta
    if (globalScale > 1.2) {
      ctx.font = `${fontSize}px Sans-Serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = canvasColors.labelColor;
      ctx.fillText(label.substring(0, 20) + (label.length > 20 ? '...' : ''), node.x, node.y + (node.val || 4) + fontSize);
    }

    return true;
  };

  // Canvas mode: Full screen graph with minimal UI
  if (canvas) {
    return (
      <div className="relative w-full h-full bg-surface" style={{ minHeight: 0 }}>
        <p className="absolute top-2 left-4 right-16 z-[5] text-[0.65rem] text-ink-muted leading-snug pointer-events-none sm:max-w-md">
          {t('content:graphSectionIntro')}
        </p>
        {error && (
          <div className="absolute top-4 left-4 z-10 max-w-sm">
            <p className="text-sm text-signal-error p-4">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <span className="text-sm text-ink-muted">···</span>
          </div>
        ) : (
          <>
            {graphData.nodes.length > 0 ? (
              <div
                className="absolute inset-0 touch-none select-none overscroll-none [&_canvas]:touch-none"
                style={{ touchAction: 'none', overscrollBehavior: 'none' }}
              >
                <ForceGraph2D
                  ref={fgRef}
                  graphData={graphData}
                  nodeRelSize={8}
                  nodeCanvasObject={nodeCanvasObject}
                  linkWidth={() => 0.5}
                  linkColor={link => (link.color || canvasColors.edgeColor) + canvasColors.edgeAlphaHex}
                  linkDirectionalArrowLength={3}
                  linkDirectionalArrowRelPos={1}
                  linkCurvature={0.1}
                  onNodeClick={handleNodeClick}
                  onLinkClick={handleLinkClick}
                  warmupTicks={200}
                  cooldownTicks={500}
                  d3AlphaDecay={0.01}
                  d3VelocityDecay={0.3}
                  enableZoomPanInteraction
                  enableZoomInteraction
                  enablePanInteraction
                  enableNodeDrag={!touchLikeGraphNav}
                  nodeLabel={node => {
                    const typeStr = String(node.type || '');
                    const typeLabel = typeStr ? (typeStr.split('/').pop() || typeStr) : 'unknown';
                    return `${node.label} (${typeLabel})`;
                  }}
                  width={canvasGraphWidth}
                  height={canvasGraphHeight}
                />
              </div>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <p className="text-ink-muted text-base mb-2">{t('content:noGraphData')}</p>
                <p className="text-ink-muted text-sm">{t('content:noGraphDataHint')}</p>
              </div>
            )}

            {/* Minimal Legend - Top Right */}
            <div className="absolute top-4 right-4 z-10 p-3 rounded bg-surface-overlay/90 backdrop-blur border border-border-subtle min-w-[180px]">
              <div className="flex flex-col gap-4">
                {/* Nodos */}
                <div>
                  <span className="text-xs text-ink-muted font-semibold uppercase tracking-widest block mb-1">
                    {t('content:nodes').toUpperCase()}
                  </span>
                  <div className="flex flex-col gap-1 max-h-[min(45vh,280px)] overflow-y-auto pr-1">
                    {[...Object.entries(dynamicColors.nodes)]
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([typeKey, color]) => (
                      <div key={`node-${typeKey}`} className="flex items-center gap-2">
                        <span style={{ backgroundColor: color }} className="w-2.5 h-2.5 rounded-full shrink-0 ring-1 ring-ink/20" />
                        <span className="text-ink-muted text-[0.65rem]">
                          {typeKey.replace(/_/g, ' ')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Relaciones */}
                <div>
                  <span className="text-xs text-ink-muted font-semibold uppercase tracking-widest block mb-1">
                    {t('content:relationships').toUpperCase()}
                  </span>
                  <div className="flex flex-col gap-1 max-h-[min(45vh,280px)] overflow-y-auto pr-1">
                    {[...Object.entries(dynamicColors.relationships)]
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([typeKey, color]) => (
                      <div key={`rel-${typeKey}`} className="flex items-center gap-2">
                        <span style={{ backgroundColor: color }} className="w-4 h-0.5 rounded shrink-0" />
                        <span className="text-ink-muted text-[0.65rem]">
                          {typeKey.replace(/_/g, ' ')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Panel - Bottom Left */}
            <div className="absolute bottom-4 left-4 z-10 p-3 rounded bg-surface-overlay/90 backdrop-blur border border-border-subtle min-w-[200px]">
              <span className="text-xs text-ink-muted font-semibold uppercase tracking-widest block mb-3">
                {t('content:statistics').toUpperCase()}
              </span>
              <div className="flex flex-col gap-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-ink-muted">{t('content:nodes').toUpperCase()}:</span>
                  <span className="text-xs font-semibold text-ink-secondary px-2 py-0.5 rounded bg-surface-hover">
                    {graphData.nodes.length}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-ink-muted">{t('content:relationships').toUpperCase()}:</span>
                  <span className="text-xs font-semibold text-ink-secondary px-2 py-0.5 rounded bg-surface-hover">
                    {graphData.links.length}
                  </span>
                </div>
                {stats && stats.people > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-ink-muted">{t('content:people').toUpperCase()}:</span>
                    <span className="text-xs font-semibold text-ink-secondary">{stats.people}</span>
                  </div>
                )}
                {stats && stats.graves > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-ink-muted">{t('content:graves').toUpperCase()}:</span>
                    <span className="text-xs font-semibold text-ink-secondary">{stats.graves}</span>
                  </div>
                )}
                {stats && stats.documents > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-ink-muted">{t('content:documents').toUpperCase()}:</span>
                    <span className="text-xs font-semibold text-ink-secondary">{stats.documents}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Floating controls — z above mobile drawer so +/- stay usable */}
            <div className="absolute bottom-4 right-4 flex items-center gap-2 z-[45]">
              <button
                type="button"
                onClick={() => fgRef.current?.zoomToFit(400)}
                className={zoomControlClass}
                aria-label={t('content:graphZoomReset')}
              >
                ⟲
              </button>
              <button
                type="button"
                onClick={() => fgRef.current?.zoom(fgRef.current?.zoom() * 0.8)}
                className={zoomControlClass}
                aria-label={t('content:graphZoomOut')}
              >
                −
              </button>
              <button
                type="button"
                onClick={() => fgRef.current?.zoom(fgRef.current?.zoom() * 1.25)}
                className={zoomControlClass}
                aria-label={t('content:graphZoomIn')}
              >
                +
              </button>
            </div>
          </>
        )}

        {selectedNode && (
          <button
            type="button"
            className="fixed inset-0 z-20 bg-ink/30 sm:hidden"
            aria-label={t('content:graphClosePanel')}
            onClick={() => setSelectedNode(null)}
          />
        )}

        {/* Node inspection panel — canvas mode */}
        {selectedNode && (
          <div
            className="fixed inset-x-0 bottom-0 max-h-[85vh] sm:max-h-none sm:absolute sm:inset-y-0 sm:right-0 sm:left-auto sm:bottom-auto w-full sm:w-72 sm:h-full bg-surface-raised/98 backdrop-blur border border-border-strong sm:border-l sm:border-t-0 rounded-t-xl sm:rounded-none flex flex-col overflow-hidden z-30 shadow-2xl"
          >
            <div className="flex-shrink-0 p-4 pb-2 space-y-4">
              <div className="flex items-center justify-between gap-2">
                <span className="text-label text-ink-muted uppercase tracking-widest">{t('content:nodePanelTitle')}</span>
                <button
                  type="button"
                  onClick={() => setSelectedNode(null)}
                  className="min-w-[2.5rem] min-h-[2.5rem] rounded-lg border border-border-strong text-ink-secondary hover:bg-surface-hover text-lg leading-none"
                  aria-label={t('content:graphClosePanel')}
                >
                  ✕
                </button>
              </div>
              <div className="pl-3 border-l-2 border-accent space-y-1">
                <p className="text-sm font-medium text-ink break-words">
                  {propsLoading ? (selectedNode.label || selectedNode.id) : panelIdentity.title}
                </p>
                <p className="text-label text-ink-muted">{getEntityTypeLabel(selectedNode)}</p>
                <p className="text-[0.65rem] text-ink-muted leading-snug">
                  <span className="text-ink-muted">{t('content:graphNodeInternalId')}:</span>{' '}
                  {selectedNode.id}
                  {panelIdentity.recordId ? (
                    <>
                      {' · '}
                      <span className="text-ink-muted">{t('content:graphNodeRecordId')}:</span>{' '}
                      {panelIdentity.recordId}
                    </>
                  ) : null}
                </p>
              </div>
            </div>
            <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
              <div className="flex-shrink-0 max-h-[min(45vh,280px)] overflow-y-auto px-4">
                {propsLoading ? (
                  <p className="text-label text-ink-muted animate-pulse">···</p>
                ) : (
                  <div className="space-y-2">
                    {nodeProps.map(([key, val], i) => (
                      <div key={i} className="space-y-1">
                        {renderNodePropRow(key, val)}
                      </div>
                    ))}
                    {nodeProps.length === 0 && !propsLoading && (
                      <p className="text-label text-ink-muted">—</p>
                    )}
                  </div>
                )}
              </div>
              {!propsLoading && (nodeNeighbors.outgoing.length > 0 || nodeNeighbors.incoming.length > 0) && (
                <div className="flex flex-col flex-1 min-h-0 border-t border-border-subtle mx-4 mb-4 px-0 pt-3">
                  <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-4">
                    {nodeNeighbors.outgoing.length > 0 && (
                      <div>
                        <p className="text-label text-ink-muted uppercase tracking-widest mb-1">{t('content:relationsOutgoing')}</p>
                        <ul className="space-y-1">
                          {nodeNeighbors.outgoing.map((row, i) => (
                            <li key={`o-${i}`}>
                              <button
                                type="button"
                                onClick={() => selectRelatedNode(row.otherId)}
                                className="text-left w-full text-sm text-ink-secondary hover:text-accent-text break-words"
                              >
                                <span className="text-label text-ink-muted">{row.rel}</span>
                                {' → '}
                                {row.otherLabel || row.otherId}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {nodeNeighbors.incoming.length > 0 && (
                      <div>
                        <p className="text-label text-ink-muted uppercase tracking-widest mb-1">{t('content:relationsIncoming')}</p>
                        <ul className="space-y-1">
                          {nodeNeighbors.incoming.map((row, i) => (
                            <li key={`i-${i}`}>
                              <button
                                type="button"
                                onClick={() => selectRelatedNode(row.otherId)}
                                className="text-left w-full text-sm text-ink-secondary hover:text-accent-text break-words"
                              >
                                <span className="text-label text-ink-muted">{row.rel}</span>
                                {' ← '}
                                {row.otherLabel || row.otherId}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Legacy fullscreen mode (for compatibility)
  return (
    <div className="relative w-full h-full bg-surface flex flex-col md:flex-row" style={{ minHeight: 0 }}>
      {/* Loading state */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <span className="text-sm text-ink-muted">···</span>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <p className="text-sm text-signal-error p-4">{error}</p>
        </div>
      )}

      {/* View toggle */}
      <div className="absolute top-4 right-4 flex items-center gap-4 z-10">
        <button
          onClick={() => setViewMode('graph')}
          className={`text-xs uppercase tracking-widest transition-colors ${viewMode === 'graph' ? 'text-accent-text' : 'text-ink-muted hover:text-ink-secondary'}`}
        >
          {t('content:graphView')}
        </button>
        <button
          onClick={() => setViewMode('stats')}
          className={`text-xs uppercase tracking-widest transition-colors ${viewMode === 'stats' ? 'text-accent-text' : 'text-ink-muted hover:text-ink-secondary'}`}
        >
          {t('content:statsView')}
        </button>
      </div>

      {/* Graph view */}
      {viewMode === 'graph' && !loading && !error && (
        <>
          {/* Legend - top left */}
          <div className="absolute top-4 left-4 z-10 p-3 rounded bg-surface-overlay/90 backdrop-blur border border-border-subtle">
            <div className="flex flex-col gap-3">
              {/* Nodos */}
              <div>
                <span className="text-xs text-ink-muted font-semibold uppercase tracking-widest block mb-1">
                  {t('content:nodes').toUpperCase()}
                </span>
                <div className="flex flex-col gap-1 max-h-[min(40vh,240px)] overflow-y-auto pr-1">
                  {[...Object.entries(dynamicColors.nodes)]
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([typeKey, color]) => (
                    <div key={`node-${typeKey}`} className="flex items-center gap-2">
                      <span style={{ backgroundColor: color }} className="w-2 h-2 rounded-full shrink-0" />
                      <span className="text-ink-muted text-[0.65rem]">
                        {typeKey.replace(/_/g, ' ')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Relaciones */}
              <div>
                <span className="text-xs text-ink-muted font-semibold uppercase tracking-widest block mb-1">
                  {t('content:relationships').toUpperCase()}
                </span>
                <div className="flex flex-col gap-1 max-h-[min(40vh,240px)] overflow-y-auto pr-1">
                  {[...Object.entries(dynamicColors.relationships)]
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([typeKey, color]) => (
                    <div key={`rel-${typeKey}`} className="flex items-center gap-2">
                      <span style={{ backgroundColor: color }} className="w-3 h-px shrink-0 rounded" />
                      <span className="text-ink-muted text-[0.65rem]">
                        {typeKey.replace(/_/g, ' ')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {graphData.nodes.length > 0 ? (
            <div
              className="flex-1 min-h-0 min-w-0 touch-none select-none [&_canvas]:touch-none"
              style={{ touchAction: 'none' }}
            >
              <ForceGraph2D
                ref={fgRef}
                graphData={graphData}
                  nodeRelSize={8}
                  nodeCanvasObject={nodeCanvasObject}
                  linkWidth={() => 0.5}
                  linkColor={link => (link.color || canvasColors.edgeColor) + canvasColors.edgeAlphaHex}
                  linkDirectionalArrowLength={3}
                  linkDirectionalArrowRelPos={1}
                  linkCurvature={0.1}
                  onNodeClick={handleNodeClick}
                  onLinkClick={handleLinkClick}
                  warmupTicks={200}
                  cooldownTicks={500}
                  d3AlphaDecay={0.01}
                  d3VelocityDecay={0.3}
                  enableZoomPanInteraction
                  enableZoomInteraction
                  enablePanInteraction
                  enableNodeDrag={!touchLikeGraphNav}
                  nodeLabel={node => {
                  const typeStr = String(node.type || '');
                  const typeLabel = typeStr ? (typeStr.split('/').pop() || typeStr) : 'unknown';
                  return `${node.label} (${typeLabel})`;
                }}
                width={fullscreen ? viewport.w : 800}
                height={fullscreen ? viewport.h : 550}
              />
            </div>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <p className="text-ink-muted text-sm">
                {t('content:noGraphData')}
              </p>
            </div>
          )}

          {/* Stats label bottom-left (only when not fullscreen) */}
          {!fullscreen && (
            <div className="absolute bottom-4 left-4 z-10 p-3 rounded bg-surface-overlay/90 backdrop-blur border border-border-subtle">
              <div className="flex flex-col gap-1 min-w-[120px]">
                <div className="flex justify-between items-center gap-4">
                  <span className="text-xs font-semibold text-ink-muted">{t('content:nodes').toUpperCase()}:</span>
                  <span className="text-xs font-semibold text-ink-secondary px-2 py-0.5 rounded bg-surface-hover">
                    {graphData.nodes.length}
                  </span>
                </div>
                <div className="flex justify-between items-center gap-4">
                  <span className="text-xs font-semibold text-ink-muted">{t('content:relationships').toUpperCase()}:</span>
                  <span className="text-xs font-semibold text-ink-secondary px-2 py-0.5 rounded bg-surface-hover">
                    {graphData.links.length}
                  </span>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Stats view */}
      {viewMode === 'stats' && stats && !fullscreen && !loading && !error && (
        <div className="absolute inset-0 overflow-auto p-6">
          <div className="grid grid-cols-2 gap-4 max-w-2xl">
            <div className="p-4 rounded bg-surface-overlay/85 backdrop-blur border border-border-subtle text-center">
              <span className="text-xs font-semibold text-ink-secondary uppercase tracking-widest block mb-2">
                {t('content:people').toUpperCase()}
              </span>
              <span className="text-4xl font-bold text-ink-secondary">{stats.people}</span>
            </div>
            <div className="p-4 rounded bg-surface-overlay/85 backdrop-blur border border-border-subtle text-center">
              <span className="text-xs font-semibold text-ink-secondary uppercase tracking-widest block mb-2">
                {t('content:massGraves').toUpperCase()}
              </span>
              <span className="text-4xl font-bold text-ink-secondary">{stats.graves}</span>
            </div>
            <div className="p-4 rounded bg-surface-overlay/85 backdrop-blur border border-accent/20 text-center">
              <span className="text-xs font-semibold text-accent-text uppercase tracking-widest block mb-2">
                {t('content:documents').toUpperCase()}
              </span>
              <span className="text-4xl font-bold text-accent-text">{stats.documents}</span>
            </div>
            <div className="p-4 rounded bg-surface-overlay/85 backdrop-blur border border-ink-muted/20 text-center">
              <span className="text-xs font-semibold text-ink-secondary uppercase tracking-widest block mb-2">
                {t('content:relationships').toUpperCase()}
              </span>
              <span className="text-4xl font-bold text-ink-secondary">{stats.relationships}</span>
            </div>
          </div>
        </div>
      )}

      {/* Floating controls — z above overlays on small screens */}
      <div className="absolute bottom-4 right-4 flex items-center gap-2 z-[45]">
        <button
          type="button"
          onClick={() => fgRef.current?.zoomToFit(400)}
          className={zoomControlClass}
          aria-label={t('content:graphZoomReset')}
        >
          ⟲
        </button>
        <button
          type="button"
          onClick={() => fgRef.current?.zoom(fgRef.current?.zoom() * 0.8)}
          className={zoomControlClass}
          aria-label={t('content:graphZoomOut')}
        >
          −
        </button>
        <button
          type="button"
          onClick={() => fgRef.current?.zoom(fgRef.current?.zoom() * 1.25)}
          className={zoomControlClass}
          aria-label={t('content:graphZoomIn')}
        >
          +
        </button>
      </div>

      {/* Stats label bottom-left */}
      {stats && viewMode === 'graph' && (
        <div className="absolute bottom-4 left-4 z-10">
          <span className="text-xs text-ink-muted">
            {stats.nodeCount ?? graphData.nodes.length} {t('content:entities')} · {stats.relationshipCount ?? graphData.links.length} {t('content:relationsCount')}
          </span>
        </div>
      )}

      {/* Node inspection panel */}
      {selectedNode && (
        <div className="w-full sm:w-72 max-h-[80vh] sm:max-h-none sm:h-full flex-shrink-0 bg-surface-raised border-l border-border-subtle flex flex-col min-h-0 overflow-hidden">
          <div className="flex-shrink-0 p-4 pb-2 space-y-4">
            <div className="flex items-center justify-between gap-2">
              <span className="text-label text-ink-muted uppercase tracking-widest">{t('content:nodePanelTitle')}</span>
              <button
                type="button"
                onClick={() => setSelectedNode(null)}
                className="min-w-[2.5rem] min-h-[2.5rem] rounded-lg border border-border-strong text-ink-secondary hover:bg-surface-hover"
                aria-label={t('content:graphClosePanel')}
              >
                ✕
              </button>
            </div>

            <div className="pl-3 border-l-2 border-accent space-y-1">
              <p className="text-sm font-medium text-ink break-words">
                {propsLoading ? (selectedNode.label || selectedNode.id) : panelIdentity.title}
              </p>
              <p className="text-label text-ink-muted">{getEntityTypeLabel(selectedNode)}</p>
              <p className="text-[0.65rem] text-ink-muted leading-snug">
                <span className="text-ink-muted">{t('content:graphNodeInternalId')}:</span>{' '}
                {selectedNode.id}
                {panelIdentity.recordId ? (
                  <>
                    {' · '}
                    <span className="text-ink-muted">{t('content:graphNodeRecordId')}:</span>{' '}
                    {panelIdentity.recordId}
                  </>
                ) : null}
              </p>
            </div>
          </div>

          <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
            <div className="flex-shrink-0 max-h-[min(45vh,280px)] overflow-y-auto px-4">
              {propsLoading ? (
                <p className="text-label text-ink-muted animate-pulse">···</p>
              ) : (
                <div className="space-y-2">
                  {nodeProps.map(([key, val], i) => (
                    <div key={i} className="space-y-1">
                      {renderNodePropRow(key, val)}
                    </div>
                  ))}
                  {nodeProps.length === 0 && !propsLoading && (
                    <p className="text-label text-ink-muted">—</p>
                  )}
                </div>
              )}
            </div>
            {!propsLoading && (nodeNeighbors.outgoing.length > 0 || nodeNeighbors.incoming.length > 0) && (
              <div className="flex flex-col flex-1 min-h-0 border-t border-border-subtle mx-4 mb-4 px-0 pt-3">
                <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-4">
                  {nodeNeighbors.outgoing.length > 0 && (
                    <div>
                      <p className="text-label text-ink-muted uppercase tracking-widest mb-1">{t('content:relationsOutgoing')}</p>
                      <ul className="space-y-1">
                        {nodeNeighbors.outgoing.map((row, i) => (
                          <li key={`lo-${i}`}>
                            <button
                              type="button"
                              onClick={() => selectRelatedNode(row.otherId)}
                              className="text-left w-full text-sm text-ink-secondary hover:text-accent-text break-words"
                            >
                              <span className="text-label text-ink-muted">{row.rel}</span>
                              {' → '}
                              {row.otherLabel || row.otherId}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {nodeNeighbors.incoming.length > 0 && (
                    <div>
                      <p className="text-label text-ink-muted uppercase tracking-widest mb-1">{t('content:relationsIncoming')}</p>
                      <ul className="space-y-1">
                        {nodeNeighbors.incoming.map((row, i) => (
                          <li key={`li-${i}`}>
                            <button
                              type="button"
                              onClick={() => selectRelatedNode(row.otherId)}
                              className="text-left w-full text-sm text-ink-secondary hover:text-accent-text break-words"
                            >
                              <span className="text-label text-ink-muted">{row.rel}</span>
                              {' ← '}
                              {row.otherLabel || row.otherId}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RDFView;
